import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Snackbar,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import FlexBox from 'components/flexbox/FlexBox';
import { H5, H6, Small, Tiny } from 'components/Typography';
import Add from 'icons/Add';
import DeleteIcon from 'icons/DeleteIcon';
import { Camera, ChevronRight, Edit, Link2, Minus, Save } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { customShadows } from 'theme/shadows';

const boardsData = [
  {
    id: 1,
    title: '메인',
    parentId: null,
    order: 0,
    rotationInterval: 3,
    banners: [
      {
        id: 'main-1',
        title: '정책자금, 정부지원금',
        subtitle: '2023년 네이버 1위 출신',
        buttonText: '강의 자세히 보기',
        buttonLink: '',
        imageName: '',
        imageLink: '',
        hideButton: false,
        imagePreview: '',
      },
      {
        id: 'main-2',
        title: '',
        subtitle: '',
        buttonText: '',
        buttonLink: '',
        imageName: '',
        imageLink: '',
        hideButton: false,
        imagePreview: '',
      },
      {
        id: 'main-3',
        title: '',
        subtitle: '',
        buttonText: '',
        buttonLink: '',
        imageName: '',
        imageLink: '',
        hideButton: false,
        imagePreview: '',
      },
      {
        id: 'main-4',
        title: '',
        subtitle: '',
        buttonText: '',
        buttonLink: '',
        imageName: '',
        imageLink: '',
        hideButton: false,
        imagePreview: '',
      },
      {
        id: 'main-5',
        title: '',
        subtitle: '',
        buttonText: '',
        buttonLink: '',
        imageName: '',
        imageLink: '',
        hideButton: false,
        imagePreview: '',
      },
    ],
  },
  {
    id: 2,
    title: '커뮤니티',
    parentId: null,
    order: 1,
    rotationInterval: 4,
    banners: [
      {
        id: 'community-1',
        title: '정책자금, 정부지원금/최고 5억받는 초특급 강의',
        subtitle: '2023년 네이버 1위 출신',
        buttonText: '강의 자세히 보기',
        buttonLink: '',
        imageName: '',
        imageLink: '',
        hideButton: false,
        imagePreview: '',
      },
      {
        id: 'community-2',
        title: '',
        subtitle: '',
        buttonText: '',
        buttonLink: '',
        imageName: '',
        imageLink: '',
        hideButton: false,
        imagePreview: '',
      },
      {
        id: 'community-3',
        title: '',
        subtitle: '',
        buttonText: '',
        buttonLink: '',
        imageName: '',
        imageLink: '',
        hideButton: false,
        imagePreview: '',
      },
    ],
  },
  {
    id: 5,
    title: '문서 자료실',
    parentId: 2,
    order: 0,
    rotationInterval: 5,
    banners: [],
  },
  {
    id: 3,
    title: '자료실',
    parentId: null,
    order: 2,
    rotationInterval: 5,
    banners: [],
  },
  {
    id: 4,
    title: '꿀정보',
    parentId: null,
    order: 3,
    rotationInterval: 5,
    banners: [],
  },
];

export default function BannerManagement() {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedBoardId, setSelectedBoardId] = useState(2);
  const [boards, setBoards] = useState(boardsData);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const titleEditCancelledRef = useRef(false);
  const [isSaveAlertOpen, setIsSaveAlertOpen] = useState(false);

  const selectedBoard = boards.find(board => board.id === selectedBoardId);
  const parentBoards = boards
    .filter(board => board.parentId === null)
    .sort((a, b) => a.order - b.order);

  useEffect(() => {
    setEditedTitle(selectedBoard?.title || '');
    setIsEditingTitle(false);
    titleEditCancelledRef.current = false;
  }, [selectedBoardId, selectedBoard?.title]);

  const getRegisteredBannerCount = board => board.banners?.length || 0;

  const selectedBanners = useMemo(
    () => selectedBoard?.banners || [],
    [selectedBoard]
  );

  const createEmptyBanner = (boardId = selectedBoardId) => ({
    id: `${boardId}-${Date.now()}`,
    title: '',
    subtitle: '',
    buttonText: '',
    buttonLink: '',
    imageName: '',
    imageLink: '',
    hideButton: false,
    imagePreview: '',
  });

  const updateBoard = updater => {
    setBoards(prev =>
      prev.map(board =>
        board.id === selectedBoardId ? { ...board, ...updater(board) } : board
      )
    );
  };

  const startEditingTitle = () => {
    if (!selectedBoard) return;
    titleEditCancelledRef.current = false;
    setEditedTitle(selectedBoard.title);
    setIsEditingTitle(true);
  };

  const saveTitle = () => {
    if (!selectedBoard) return;
    const newTitle = editedTitle.trim() || selectedBoard.title;
    updateBoard(() => ({ title: newTitle }));
    setEditedTitle(newTitle);
    setIsEditingTitle(false);
  };

  const cancelTitleEdit = () => {
    titleEditCancelledRef.current = true;
    setEditedTitle(selectedBoard?.title || '');
    setIsEditingTitle(false);
  };

  const handleTitleBlur = () => {
    if (titleEditCancelledRef.current) {
      titleEditCancelledRef.current = false;
      return;
    }
    saveTitle();
  };

  const handleTitleKeyDown = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
      saveTitle();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      cancelTitleEdit();
    }
  };

  const handleRotationChange = value => {
    updateBoard(() => ({ rotationInterval: value }));
  };

  const handleBannerChange = (bannerId, key, value) => {
    updateBoard(board => ({
      banners: board.banners.map(banner =>
        banner.id === bannerId ? { ...banner, [key]: value } : banner
      ),
    }));
  };

  const handleAddBanner = () => {
    updateBoard(board => ({
      banners: [...board.banners, createEmptyBanner(selectedBoardId)],
    }));
  };

  const handleImageUpload = (bannerId, event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);

    updateBoard(board => ({
      banners: board.banners.map(banner => {
        if (banner.id !== bannerId) return banner;
        if (banner.imagePreview) {
          URL.revokeObjectURL(banner.imagePreview);
        }
        return {
          ...banner,
          imageName: file.name,
          imagePreview: previewUrl,
        };
      }),
    }));
  };

  const handleCreateBoard = () => {
    const newBoardId = Date.now();
    const minOrder =
      parentBoards.length > 0
        ? Math.min(...parentBoards.map(board => board.order))
        : 0;

    const newBoard = {
      id: newBoardId,
      title: `새 배너 ${parentBoards.length + 1}`,
      parentId: null,
      order: minOrder - 1,
      rotationInterval: '',
      banners: [createEmptyBanner(newBoardId)],
    };

    setBoards(prev => [...prev, newBoard]);
    setSelectedBoardId(newBoardId);
  };

  const handleSaveClick = () => {
    // TODO: connect to real save API when available
    setIsSaveAlertOpen(true);
  };

  const handleSaveAlertClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setIsSaveAlertOpen(false);
  };

  const handleRemoveBanner = bannerId => {
    updateBoard(board => ({
      banners: board.banners.filter(banner => banner.id !== bannerId),
    }));
  };

  const handleDeleteBoard = () => {
    if (!selectedBoard) return;

    setBoards(prevBoards => {
      const updatedBoards = prevBoards.filter(
        board => board.id !== selectedBoardId
      );

      if (updatedBoards.length === 0) {
        setSelectedBoardId(null);
        return updatedBoards;
      }

      // If the currently selected board was removed, select the first remaining one
      if (!updatedBoards.some(board => board.id === selectedBoardId)) {
        setSelectedBoardId(updatedBoards[0].id);
      }

      return updatedBoards;
    });
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={4}>
        <Card
          sx={{
            padding: '24px',
            borderRadius: '18px',
            boxShadow: customShadows.card,
            backgroundColor: '#ffffff',
            border: 'none',
            height: 'calc(100vh - 120px)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <FlexBox alignItems="center" mb={3} gap={1}>
            <Edit
              size={22}
              color={theme.palette.primary.mainBlue}
              strokeWidth={2}
            />
            <H5 sx={{ color: '#151515', fontWeight: 600 }}>배너 관리 에디터</H5>
          </FlexBox>

          <Box
            sx={{
              flex: 1,
              mb: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              overflowY: 'auto',
              pr: 1,
            }}
          >
            {parentBoards.map(board => {
              const isSelected = selectedBoardId === board.id;
              const bannerCount = getRegisteredBannerCount(board);

              return (
                <Card
                  key={board.id}
                  onClick={() => setSelectedBoardId(board.id)}
                  sx={{
                    flexShrink: 0,
                    padding: '20px',
                    borderRadius: '18px',
                    border: isSelected
                      ? `1.5px solid ${theme.palette.primary.mainBlue}`
                      : 'none',
                    backgroundColor: isSelected ? '#ECF1FF' : '#F8F8F8',
                    boxShadow: isSelected ? customShadows.card : 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: theme.palette.primary.mainBlue,
                    },
                  }}
                >
                  <FlexBox justifyContent="space-between" alignItems="center">
                    <Box>
                      <H6
                        sx={{
                          color: isSelected
                            ? theme.palette.primary.mainBlue
                            : '#151515',
                          fontWeight: 700,
                          mb: 0.5,
                        }}
                      >
                        {board.title}
                      </H6>
                      <Small
                        sx={{
                          color: '#555B6B',
                          fontWeight: 500,
                        }}
                      >
                        배너 {bannerCount}개 등록 중
                      </Small>
                    </Box>
                    <ChevronRight
                      size={20}
                      color={
                        isSelected
                          ? theme.palette.primary.mainBlue
                          : theme.palette.primary.greytxt
                      }
                    />
                  </FlexBox>
                </Card>
              );
            })}
          </Box>

          <Button
            fullWidth
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateBoard}
            sx={{
              backgroundColor: theme.palette.primary.mainBlue,
              color: '#ffffff',
              borderRadius: '12px',
              padding: '12px',
              fontSize: '15px',
              fontWeight: 500,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: theme.palette.primary.mainBlueHover,
                boxShadow: 'none',
              },
            }}
          >
            새 배너 만들기
          </Button>
        </Card>
      </Grid>

      <Grid item xs={12} md={8}>
        <Card
          sx={{
            padding: '24px',
            borderRadius: '18px',
            boxShadow: customShadows.card,
            backgroundColor: '#ffffff',
            border: 'none',
          }}
        >
          <Box
            mb={3}
            sx={{
              display: 'flex',
              flexDirection: isTablet ? 'column' : 'row',
              alignItems: isTablet ? 'flex-start' : 'center',
              justifyContent: 'space-between',
              gap: isTablet ? 2 : 0,
            }}
          >
            <Box>
              <Box
                onClick={() => {
                  if (!isEditingTitle) startEditingTitle();
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                  mb: 0.5,
                  cursor: isEditingTitle ? 'default' : 'text',
                }}
              >
                {isEditingTitle ? (
                  <TextField
                    value={editedTitle}
                    autoFocus
                    onChange={event => setEditedTitle(event.target.value)}
                    onBlur={handleTitleBlur}
                    onKeyDown={handleTitleKeyDown}
                    size="small"
                    sx={{
                      width: 'auto',
                      minWidth: '140px',
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#F8F8F8',
                        borderRadius: '10px',
                        paddingRight: 0,
                        '& fieldset': {
                          border: 'none',
                        },
                        '&:hover fieldset': {
                          border: 'none',
                        },
                        '&.Mui-focused fieldset': {
                          border: 'none',
                          boxShadow: '0 0 0 1px rgba(92,115,255,0.2)',
                        },
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: '6px 12px',
                        fontSize: '22px',
                        fontWeight: 700,
                        color: '#111322',
                      },
                    }}
                  />
                ) : (
                  <H5>{selectedBoard?.title}</H5>
                )}
                <H5>배너 관리</H5>
              </Box>
              <Tiny sx={{ color: '#7A7F8C' }}>
                배너 콘텐츠를 순서대로 등록하고 관리할 수 있어요.
              </Tiny>
            </Box>

            <FlexBox
              gap={1}
              sx={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: isTablet ? 'flex-start' : 'flex-end',
                mt: isTablet ? 2 : 0,
                width: isTablet ? '100%' : 'auto',
              }}
            >
              <Button
                variant="contained"
                startIcon={<Save size={18} />}
                onClick={handleSaveClick}
                sx={{
                  width: isTablet ? '100%' : 'auto',
                  backgroundColor: theme.palette.primary.mainBlue,
                  color: '#ffffff',
                  borderRadius: '12px',
                  padding: '4px 16px',
                  fontSize: '14px',
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: theme.palette.primary.mainBlueHover,
                    boxShadow: 'none',
                  },
                }}
              >
                저장
              </Button>
              <Button
                variant="outlined"
                startIcon={<DeleteIcon sx={{ fontSize: '16px' }} />}
                onClick={handleDeleteBoard}
                sx={{
                  width: isTablet ? '100%' : 'auto',
                  borderRadius: '12px',
                  padding: '4px 16px',
                  fontSize: '14px',
                  fontWeight: 500,
                  textTransform: 'none',
                  backgroundColor: theme.palette.primary.grey10,
                  borderColor: '#E4E7EC',
                  color: '#7A7F8C',
                  '&:hover': {
                    borderColor: '#CBD0DA',
                  },
                }}
              >
                삭제
              </Button>
            </FlexBox>
          </Box>

          <Box mb={3}>
            <Small sx={{ color: '#5D6473', mb: 1, display: 'block' }}>
              배너 전환 주기
            </Small>
            <TextField
              fullWidth
              placeholder="배너 전환 주기를 입력해주세요."
              value={selectedBoard?.rotationInterval || ''}
              onChange={event => handleRotationChange(event.target.value)}
              InputProps={{
                sx: {
                  backgroundColor: '#F8F8F8',
                  borderRadius: '14px',
                  '& fieldset': { border: 'none' },
                  fontSize: '15px',
                },
              }}
            />
          </Box>

          <Box display="flex" flexDirection="column" gap={3}>
            {selectedBanners.map((banner, index) => (
              <Box key={banner.id}>
                <Box
                  sx={{
                    borderTop: `1px solid ${theme.palette.primary.grey10}`,
                    mb: 2,
                  }}
                />
                <FlexBox
                  alignItems="center"
                  justifyContent="space-between"
                  mb={2}
                >
                  <H5
                    sx={{
                      color: theme.palette.primary.mainBlue,
                      fontWeight: 600,
                    }}
                  >
                    #{index + 1}
                  </H5>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveBanner(banner.id)}
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.primary.grey10,
                      '&:hover': {
                        backgroundColor: '#FFE5E5',
                      },
                    }}
                  >
                    <Minus size={18} color="#FF3B30" />
                  </IconButton>
                </FlexBox>

                <Box display="flex" flexDirection="column" gap={2.5}>
                  <Box>
                    <Small sx={{ color: '#5D6473', mb: 1, display: 'block' }}>
                      제목
                    </Small>
                    <TextField
                      fullWidth
                      placeholder="정책자금, 정부지원금/최고 5억받는 초특급 강의"
                      value={banner.title}
                      onChange={event =>
                        handleBannerChange(
                          banner.id,
                          'title',
                          event.target.value
                        )
                      }
                      InputProps={{
                        sx: {
                          backgroundColor: '#F8F8F8',
                          borderRadius: '14px',
                          '& fieldset': { border: 'none' },
                        },
                      }}
                    />
                  </Box>

                  <Box>
                    <Small sx={{ color: '#5D6473', mb: 1, display: 'block' }}>
                      부제목
                    </Small>
                    <TextField
                      fullWidth
                      placeholder="2023년 네이버 1위 출신"
                      value={banner.subtitle}
                      onChange={event =>
                        handleBannerChange(
                          banner.id,
                          'subtitle',
                          event.target.value
                        )
                      }
                      InputProps={{
                        sx: {
                          backgroundColor: '#F8F8F8',
                          borderRadius: '14px',
                          '& fieldset': { border: 'none' },
                        },
                      }}
                    />
                  </Box>

                  <Box display="flex" gap={2} alignItems="flex-end">
                    <Box flex={1}>
                      <Small sx={{ color: '#5D6473', mb: 1, display: 'block' }}>
                        버튼 텍스트
                      </Small>
                      <TextField
                        fullWidth
                        placeholder="강의 자세히 보기"
                        value={banner.buttonText}
                        onChange={event =>
                          handleBannerChange(
                            banner.id,
                            'buttonText',
                            event.target.value
                          )
                        }
                        InputProps={{
                          sx: {
                            backgroundColor: '#F8F8F8',
                            borderRadius: '14px',
                            '& fieldset': { border: 'none' },
                          },
                        }}
                      />
                    </Box>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={banner.hideButton}
                          onChange={event =>
                            handleBannerChange(
                              banner.id,
                              'hideButton',
                              event.target.checked
                            )
                          }
                          sx={{
                            '&.Mui-checked': {
                              color: theme.palette.primary.mainBlue,
                            },
                          }}
                        />
                      }
                      label="버튼 숨기기"
                      sx={{ color: '#5D6473', whiteSpace: 'nowrap' }}
                    />
                  </Box>

                  <Box>
                    <Small sx={{ color: '#5D6473', mb: 1, display: 'block' }}>
                      버튼 링크
                    </Small>
                    <TextField
                      fullWidth
                      placeholder="링크 입력"
                      value={banner.buttonLink}
                      onChange={event =>
                        handleBannerChange(
                          banner.id,
                          'buttonLink',
                          event.target.value
                        )
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton edge="end" size="small">
                              <Link2 size={18} />
                            </IconButton>
                          </InputAdornment>
                        ),
                        sx: {
                          backgroundColor: '#F8F8F8',
                          borderRadius: '14px',
                          '& fieldset': { border: 'none' },
                        },
                      }}
                    />
                  </Box>

                  <Box>
                    <Small sx={{ color: '#5D6473', mb: 1, display: 'block' }}>
                      배너 이미지 업로드
                    </Small>
                    <Box
                      component="label"
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#F8F8F8',
                        borderRadius: '16px',
                        padding: banner.imagePreview ? 0 : '48px 20px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        height: '200px',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                          backgroundColor: banner.imagePreview
                            ? 'transparent'
                            : '#E8EAED',
                        },
                        '&:hover .upload-overlay': {
                          opacity: 1,
                        },
                      }}
                    >
                      <input
                        hidden
                        type="file"
                        accept="image/*"
                        onChange={event => handleImageUpload(banner.id, event)}
                      />
                      {banner.imagePreview ? (
                        <>
                          <Box
                            component="img"
                            src={banner.imagePreview}
                            alt="배너 미리보기"
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                          <Box
                            className="upload-overlay"
                            sx={{
                              position: 'absolute',
                              inset: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#FFFFFF',
                              fontWeight: 600,
                              fontSize: '14px',
                              backgroundColor: 'rgba(0,0,0,0.35)',
                              opacity: 0,
                              transition: 'opacity 0.2s ease',
                            }}
                          >
                            변경하기
                          </Box>
                        </>
                      ) : (
                        <>
                          <Camera size={30} color="#9CA3AF" strokeWidth={1.5} />
                          <Tiny
                            sx={{
                              color: '#7A7F8C',
                              mt: 1.5,
                              textAlign: 'center',
                            }}
                          >
                            클릭하여 파일 선택
                          </Tiny>
                          <Small
                            sx={{
                              color: '#9CA3AF',
                              mt: 0.5,
                              textAlign: 'center',
                            }}
                          >
                            (3:4 사이즈)
                          </Small>
                        </>
                      )}
                    </Box>
                    {banner.imageName && (
                      <Tiny sx={{ color: '#7A7F8C', mt: 1 }}>
                        {banner.imageName}
                      </Tiny>
                    )}
                  </Box>

                  <Box>
                    <Small sx={{ color: '#5D6473', mb: 1, display: 'block' }}>
                      이미지 하이퍼링크
                    </Small>
                    <TextField
                      fullWidth
                      placeholder="링크 입력"
                      value={banner.imageLink}
                      onChange={event =>
                        handleBannerChange(
                          banner.id,
                          'imageLink',
                          event.target.value
                        )
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton edge="end" size="small">
                              <Link2 size={18} />
                            </IconButton>
                          </InputAdornment>
                        ),
                        sx: {
                          backgroundColor: '#F8F8F8',
                          borderRadius: '14px',
                          '& fieldset': { border: 'none' },
                        },
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            ))}

            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={handleAddBanner}
              sx={{
                borderRadius: '14px',
                padding: '14px',
                fontSize: '15px',
                fontWeight: 500,
                textTransform: 'none',
                borderColor: theme.palette.primary.mainBlue,
                color: theme.palette.primary.mainBlue,
                '&:hover': {
                  borderColor: theme.palette.primary.mainBlueHover,
                  backgroundColor: '#EEF2FF',
                },
              }}
            >
              배너 추가하기
            </Button>
          </Box>
        </Card>

        <Snackbar
          open={isSaveAlertOpen}
          autoHideDuration={3000}
          onClose={handleSaveAlertClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={handleSaveAlertClose}
            severity="success"
            sx={{
              bgcolor: '#111322',
              color: '#ffffff',
              boxShadow: customShadows.card,
              borderRadius: '10px',
            }}
          >
            배너 설정이 저장되었습니다.
          </Alert>
        </Snackbar>
      </Grid>
    </Grid>
  );
}
